import { Divider, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import AccountCircleIcon from '@material-ui/icons/AccountCircle'
import CallIcon from '@material-ui/icons/Call'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom'
import PersonOutlineIcon from '@material-ui/icons/PersonOutline'
import WarningIcon from '@material-ui/icons/Warning'
import parse from 'html-react-parser'
import { ReactNode } from 'react'

import { gregorianToJalaali } from '../../../../helpers/date'
import { addCommaToPrice } from '../../../../helpers/numbers'
import { persianRegex } from '../../../../helpers/regex'
import { stripHtmlTags } from '../../../../helpers/stripHTML'
import { getTranslate, lang } from '../../../../localization'
import { RoomStay } from '../../../../types/bookingDetails'
import { Currency } from '../../../../types/currency'
import FailureDetails from './FailureDetails'
import { generatePolicyTextString } from './generatePolicyText'
import { useStyles } from './styles/index.styles'

type Props = {
  room: RoomStay | undefined
  currency: Currency
  adminEditMode: boolean
  index: number
  children?: ReactNode
}
const RoomDetails = ({ room, currency, index, children }: Props) => {
  const classes = useStyles()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <div className={classes.root}>
      <div className={classes.top}>
        <Typography variant="h4" gutterBottom className={classes.title}>
          {lang === 'fa'
            ? `${getTranslate('Details')} ${getTranslate('Room')} ${index}`
            : `${getTranslate('Room')} ${index} ${getTranslate('Details')}`}
        </Typography>
      </div>
      <Divider />
      <div className={classes.container}>
        <div className={classes.left}>
          <div className={classes.header}>
            <div>
              <div className={classes.subHeader}>
                <Typography
                  variant="subtitle1"
                  className={classes.roomTypeText}>
                  <MeetingRoomIcon className={classes.icon} />
                  {room?.roomTypeName
                    ? `${room?.roomTypeName} (${room?.displayRatePlanCode})`
                    : ''}
                </Typography>
              </div>
              <div className={classes.horixental}>
                <Typography variant="h6" className={classes.label}>
                  {`${getTranslate('Board Type')}: `}
                </Typography>
                <Typography variant="h6" className={classes.value}>
                  {getTranslate(room?.boardType || '') ?? ''}
                </Typography>
              </div>
            </div>
            <div className={classes.check}>
              <Typography variant="h5" className={classes.Reserve}>
                {getTranslate('Reserve Number')}:{' '}
                <span dir="ltr">{room?.displayReservationNumber || 0}</span>
              </Typography>
              <div className={classes.horixental}>
                <Typography variant="h6" className={classes.label}>
                  {`${getTranslate('Check-in')}: `}
                </Typography>
                <Typography variant="h6" className={classes.value}>
                  {lang === 'en'
                    ? room?.checkIn
                    : gregorianToJalaali(room?.checkIn || '')}
                </Typography>
              </div>
              <div className={classes.horixental}>
                <Typography variant="h6" className={classes.label}>
                  {`${getTranslate('Check-out')}: `}
                </Typography>
                <Typography variant="h6" className={classes.value}>
                  {lang === 'en'
                    ? room?.checkOut
                    : gregorianToJalaali(room?.checkOut || '')}
                </Typography>
              </div>
            </div>
          </div>
          <Divider />
          <div className={classes.section}>
            <Typography variant="h4" gutterBottom className={classes.blackText}>
              {getTranslate('Details')}
            </Typography>
            <div style={{ display: 'flex', gap: isMobile ? 8 : 16 }}>
              <div className={classes.box} style={{ flex: 1 }}>
                <div className={classes.row}>
                  <Typography variant="h6" className={classes.greyText}>
                    {getTranslate('CRS Status')}
                  </Typography>
                  <Typography
                    variant="h6"
                    className={[
                      classes.greyText,
                      room?.crsStatus === 'Cancelled' && classes.cancelText
                    ].join(' ')}>
                    {room?.crsStatus ?? '-'}
                  </Typography>
                </div>
                <div className={classes.row}>
                  <Typography variant="h6" className={classes.greyText}>
                    {getTranslate('PMS Status')}
                  </Typography>
                  <Typography
                    variant="h6"
                    className={[
                      classes.greyText,
                      room?.pmsStatus === 'Cancelled' && classes.cancelText
                    ].join(' ')}>
                    {room?.pmsStatus ?? '-'}
                  </Typography>
                </div>
                <div className={classes.row}>
                  <Typography variant="h6" className={classes.greyText}>
                    {getTranslate('PMS Number')}
                  </Typography>
                  <Typography variant="h6" className={classes.greyText}>
                    {room?.pmsNumber ?? '-'}
                  </Typography>
                </div>
                <div className={classes.row}>
                  <Typography variant="h6" className={classes.greyText}>
                    {getTranslate('PMS Secondary Number')}
                  </Typography>
                  <Typography variant="h6" className={classes.greyText}>
                    {room?.pmsSecondaryNumber ?? '-'}
                  </Typography>
                </div>
              </div>
              <div className={classes.box} style={{ flex: 1 }}>
                <div className={classes.row}>
                  <Typography variant="h6" className={classes.greyText}>
                    {getTranslate('Number of Adults')}
                  </Typography>
                  <Typography variant="h6" className={classes.greyText}>
                    {room?.numberOfAdults ?? 0}
                  </Typography>
                </div>
                <div className={classes.row}>
                  <Typography variant="h6" className={classes.greyText}>
                    {getTranslate('Number of Extra Beds')}
                  </Typography>
                  <Typography variant="h6" className={classes.greyText}>
                    {room?.addBed ?? ''}
                  </Typography>
                </div>
                <div className={classes.row}>
                  <Typography variant="h6" className={classes.greyText}>
                    {getTranslate('Number of Children')}
                  </Typography>
                  <Typography variant="h6" className={classes.greyText}>
                    0
                  </Typography>
                </div>
                <div className={classes.row}></div>
              </div>
            </div>
          </div>
          <div className={classes.section}>
            {room && room?.profiles.length > 0 && (
              <>
                <Typography
                  variant="h5"
                  gutterBottom
                  className={classes.blackText}>
                  {getTranslate('Guest Details')}
                </Typography>
                {room?.profiles.map((p, i) => (
                  <div className={classes.personCard} key={i}>
                    <div className={classes.iconText}>
                      <AccountCircleIcon className={classes.profileIcon} />
                      <Typography variant="h5" className={classes.blackText}>
                        {getTranslate('Adult')}
                      </Typography>
                    </div>
                    <div className={classes.detailsBox}>
                      <div className={classes.row2}>
                        {isMobile ? (
                          <PersonOutlineIcon className={classes.formIcon} />
                        ) : (
                          <Typography
                            variant={isMobile ? 'h6' : 'h5'}
                            className={classes.blackText}>
                            {`${getTranslate('Name')}: `} &nbsp;
                          </Typography>
                        )}
                        <Typography
                          variant={isMobile ? 'h6' : 'h5'}
                          className={classes.blackText}
                          style={
                            persianRegex.test(p.firstName)
                              ? { fontFamily: 'IranSans' }
                              : undefined
                          }>
                          {`${p.firstName} ${p.lastName}`}
                        </Typography>
                      </div>
                      <div className={classes.row2}>
                        {isMobile ? (
                          <CallIcon className={classes.formIcon} />
                        ) : (
                          <Typography
                            variant={isMobile ? 'h6' : 'h5'}
                            className={classes.blackText}>
                            {`${getTranslate('Mobile')}: `} &nbsp;
                          </Typography>
                        )}
                        <Typography
                          variant={isMobile ? 'h6' : 'h5'}
                          className={classes.blackText}>
                          {p.phone}
                        </Typography>
                      </div>
                      <div className={classes.row2}>
                        {isMobile ? (
                          <MailOutlineIcon className={classes.formIcon} />
                        ) : (
                          <Typography
                            variant={isMobile ? 'h6' : 'h5'}
                            className={classes.blackText}>
                            {`${getTranslate('Email')}: `} &nbsp;
                          </Typography>
                        )}
                        <Typography
                          variant={isMobile ? 'h6' : 'h5'}
                          className={classes.blackText}>
                          {p.email ?? ''}
                        </Typography>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
        <div className={classes.right}>
          <div className={classes.box}>
            <div className={classes.row}>
              <Typography variant="h6" className={classes.greyText}>
                {getTranslate('Original Price')}
              </Typography>
              <Typography variant="h6" className={classes.greyText}>
                {`${addCommaToPrice(room?.originalPrice ?? 0)} ${getTranslate(
                  currency || ''
                )}`}
              </Typography>
            </div>
            <div className={classes.row}>
              <Typography variant="h6" className={classes.greyText}>
                {getTranslate('Payable Price')}
              </Typography>
              <Typography variant="h6" className={classes.greyText}>
                {` ${addCommaToPrice(room?.payablePrice ?? 0)} ${getTranslate(
                  currency || ''
                )}`}
              </Typography>
            </div>
            <div className={classes.row}>
              <Typography variant="h6" className={classes.greyText}>
                {getTranslate('Extra Bed Price')}
              </Typography>
              <Typography variant="h6" className={classes.greyText}>
                {`${addCommaToPrice(room?.extraBedPrice ?? 0)} ${getTranslate(
                  currency || ''
                )}`}
              </Typography>
            </div>
          </div>
          <div className={classes.policy}>
            <div>
              <Typography variant="h6" className={classes.greyText}>
                {getTranslate('Cancellation Policy')}
              </Typography>
              <Typography
                variant="h6"
                className={classes.redText}
                style={{
                  direction: lang === 'fa' ? 'rtl' : 'ltr', // make layout direction dynamic
                  textAlign: lang === 'fa' ? 'right' : 'left', // align text accordingly
                  whiteSpace: 'pre-line', // keep newlines from stripHtmlTags
                  unicodeBidi: 'embed' // handle mixed RTL/LTR properly
                }}>
                {parse(
                  stripHtmlTags(
                    lang === 'fa'
                      ? // Farsi
                        room?.cancellationPolicy?.descriptionTranslatedList?.[0]
                          ?.value ||
                          generatePolicyTextString(
                            'fa',
                            getTranslate,
                            true,
                            room?.cancellationPolicy?.policies || []
                          )
                      : // English
                        room?.cancellationPolicy?.description ||
                          generatePolicyTextString(
                            'en',
                            getTranslate,
                            true,
                            room?.cancellationPolicy?.policies || []
                          )
                  )
                )}
              </Typography>
            </div>
            <div>
              <Typography variant="h6" className={classes.greyText}>
                {getTranslate('Amendment Policy')}
              </Typography>
              <Typography
                variant="h6"
                className={classes.yellowText}
                style={{
                  direction: lang === 'fa' ? 'rtl' : 'ltr', // make layout direction dynamic
                  textAlign: lang === 'fa' ? 'right' : 'left', // align text accordingly
                  whiteSpace: 'pre-line', // keep newlines from stripHtmlTags
                  unicodeBidi: 'embed' // handle mixed RTL/LTR properly
                }}>
                {parse(
                  stripHtmlTags(
                    lang === 'fa'
                      ? // Farsi
                        room?.modificationPolicy?.descriptionTranslatedList?.[0]
                          ?.value ||
                          generatePolicyTextString(
                            'fa',
                            getTranslate,
                            true,
                            room?.modificationPolicy?.policies || []
                          )
                      : // English
                        room?.modificationPolicy?.description ||
                          generatePolicyTextString(
                            'en',
                            getTranslate,
                            true,
                            room?.modificationPolicy?.policies || []
                          )
                  )
                )}
              </Typography>
            </div>
            <div>
              <Typography variant="h6" className={classes.greyText}>
                {getTranslate('Rate Policy')}
              </Typography>
              <Typography
                variant="h6"
                className={classes.blueText}
                style={{
                  direction: lang === 'fa' ? 'rtl' : 'ltr', // make layout direction dynamic
                  textAlign: lang === 'fa' ? 'right' : 'left', // align text accordingly
                  whiteSpace: 'pre-line', // keep newlines from stripHtmlTags
                  unicodeBidi: 'embed' // handle mixed RTL/LTR properly
                }}>
                {parse(
                  stripHtmlTags(
                    lang === 'fa' &&
                      room?.ratePolicyTranslatedList &&
                      room?.ratePolicyTranslatedList.length > 0
                      ? room?.ratePolicyTranslatedList[0].value
                      : room?.ratePolicy ?? ''
                  )
                )}
              </Typography>
            </div>
          </div>
        </div>
      </div>
      {room?.failedReason && room.failedReason !== '' && (
        <div className={classes.bottom}>
          <div className={classes.error}>
            <Typography variant="subtitle1" className={classes.verticalAlign}>
              <WarningIcon style={{ fontSize: 16, marginRight: 6 }} />{' '}
              {getTranslate('Failed')}
            </Typography>
            <Typography variant="h6">{room.failedReason}</Typography>
          </div>
        </div>
      )}
      {children && <div className={classes.bottom}>{children}</div>}
      {room?.technicalFailure && (
        <div className={classes.bottom}>
          <FailureDetails room={room} />
        </div>
      )}
    </div>
  )
}

export default RoomDetails
